import React, { useRef, useState, useEffect } from "react";
import Tutorial1 from "../assets/tutorial/Tutorial1Learn.svg";
import Tutorial2 from "../assets/tutorial/Tutorial2Apply.svg";
import Tutorial3 from "../assets/tutorial/Tutorial3Customize.svg";
import Tutorial4 from "../assets/tutorial/Tutorial4Use.svg";

interface TutorialPage {
  title?: string | React.ReactNode;
  description?: string;
  image?: string;
  video?: string;
}

const tutorialPages: TutorialPage[] = [
  {
    title: (
      <>
        Welcome to Psych for Life<sup>®</sup>!
      </>
    ),
    description:
      "Let's show you how the app works so you can get the most out of your experience.",
    video:
      "https://player.vimeo.com/video/1149514683?h=b4429de07a&badge=0&autopause=0&player_id=0&app_id=58479",
  },
  {
    title: "Learn Quickly",
    description:
      "Short engaging videos and text presented simply and clearly as tools you can use.",
    image: Tutorial1,
  },
  {
    title: "Apply What You've Learned",
    description:
      "Simple activities designed to help you practice using these tools.",
    image: Tutorial2,
  },
  {
    title: "Select and Customize",
    description:
      "You're an individual, your own person. Collect and personalize the tools that work best for you to create your own toolbox.",
    image: Tutorial3,
  },
  {
    title: "Use!",
    description: "Put your tools into action at home, work – wherever you are.",
    image: Tutorial4,
  },
];

interface SignUpTutorialProps {
  pageTitles?: (string | React.ReactNode)[];
}

const SignUpTutorial: React.FC<SignUpTutorialProps> = ({ pageTitles }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const sliderRef = useRef(null);

  // Use pageTitles to filter pages if provided, otherwise use all pages
  const pages = pageTitles
    ? tutorialPages.slice(0, pageTitles.length)
    : tutorialPages;

  useEffect(() => {
    // Handle slider scroll
    const slider = sliderRef.current;
    if (slider) {
      const handleScroll = () => {
        const scrollPosition = slider.scrollLeft;
        const cardWidth = slider.offsetWidth;
        const index = Math.round(scrollPosition / cardWidth);
        setCurrentPage(index);
      };

      slider.addEventListener("scroll", handleScroll);
      return () => {
        slider.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  const scrollToPage = (index: number) => {
    const slider = sliderRef.current;
    if (slider) {
      const cardWidth = slider.offsetWidth;
      slider.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
      setCurrentPage(index);
    }
  };

  const handlePrevSlide = () => {
    if (currentPage > 0) {
      scrollToPage(currentPage - 1);
    }
  };

  const handleNextSlide = () => {
    if (currentPage < pages.length - 1) {
      scrollToPage(currentPage + 1);
    }
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-2 lg:p-4 flex flex-col">
      <div className="relative flex-1 flex items-center">
        <div
          ref={sliderRef}
          className="flex-1 overflow-x-auto scroll whitespace-nowrap scroll-smooth no-scrollbar mb-2"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {pages.map((page, index) => (
            <div
              key={index}
              className="inline-flex w-full flex-shrink-0"
              style={{ scrollSnapAlign: "start" }}
            >
              <div className="flex flex-col items-center w-full px-4 pt-2 pb-2 whitespace-normal">
                <div className="mb-2 w-full flex justify-center">
                  {page.video && currentPage === index ? (
                    <div className="w-full aspect-video mt-0">
                      <iframe
                        src={page.video}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        title={
                          typeof page.title === "string" ? page.title : "Video"
                        }
                        className="w-full h-full rounded-lg"
                      />
                    </div>
                  ) : page.video && currentPage !== index ? (
                    <div className="w-full aspect-video mt-0" />
                  ) : page.image ? (
                    <img
                      src={page.image}
                      alt={typeof page.title === "string" ? page.title : ""}
                      className="w-auto h-80 object-contain"
                    />
                  ) : null}
                </div>
                <div className="w-full">
                  <h2 className="text-xl font-semibold mt-3 px-4 text-center">
                    {page.title}
                  </h2>
                  <p className="mt-2 text-gray-700 px-8 text-sm md:text-base text-center">
                    {page.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={handlePrevSlide}
          className={`absolute left-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:bg-gray-50 ${
            currentPage === 0 ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={currentPage === 0}
        >
          <svg
            className="w-6 h-6 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button
          onClick={handleNextSlide}
          className={`absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:bg-gray-50 ${
            currentPage === pages.length - 1
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          disabled={currentPage === pages.length - 1}
        >
          <svg
            className="w-6 h-6 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SignUpTutorial;
